import React from "react";
import "./styles.css";

export type DispositionKey =
  | "returnNew"
  | "returnUsed"
  | "laundry"
  | "dispose"
  | "notReturnedManko"
  | "notReturned";

export type DispositionColumn = {
  key: DispositionKey;
  title: string;
};

export type IssuedItemRow = {
  id: string;
  label: string;
  qty: number;
  values: Record<DispositionKey, number>;
};

export type ItemDispositionMatrixProps = {
  titleLeft?: string;
  columns?: DispositionColumn[];
  rows: IssuedItemRow[];
  onChange: (nextRows: IssuedItemRow[]) => void;

  readOnly?: boolean;
  className?: string;

  limitByQty?: boolean;
};

const DEFAULT_COLUMNS: DispositionColumn[] = [
  { key: "returnNew", title: "Vrátit na sklad\njako nové" },
  { key: "returnUsed", title: "Vrátit na sklad\njako použité" },
  { key: "laundry", title: "Do čistírny" },
  { key: "dispose", title: "K likvidaci" },
];

function clampInt(n: number) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.trunc(n));
}

function ItemDispositionMatrix({
  titleLeft = "Vydané položky",
  columns = DEFAULT_COLUMNS,
  rows,
  onChange,
  readOnly = false,
  className = "",
  limitByQty = true,
}: ItemDispositionMatrixProps) {
  const updateCell = (rowId: string, key: DispositionKey, raw: string) => {
    const next = rows.map((r) => {
      if (r.id !== rowId) return r;

      const nextVal = clampInt(Number(raw));
      const nextValues = { ...r.values, [key]: nextVal };

      if (!limitByQty) {
        return { ...r, values: nextValues };
      }

      const sum = columns.reduce((acc, c) => acc + (nextValues[c.key] ?? 0), 0);
      if (sum <= r.qty) return { ...r, values: nextValues };

      const over = sum - r.qty;
      const corrected = Math.max(0, nextVal - over);
      nextValues[key] = corrected;

      return { ...r, values: nextValues };
    });

    onChange(next);
  };

  const rowSum = (r: IssuedItemRow) =>
    columns.reduce((acc, c) => acc + (r.values[c.key] ?? 0), 0);

  return (
    <div className={`idm ${className}`}>
      <div className="idm__header">
        <div className="idm__hcell idm__hcell--left">{titleLeft}</div>

        {columns.map((c) => (
          <div key={c.key} className={columns.length > 4 ? "idm__hcell idm__hcell--colmore" : "idm__hcell idm__hcell--col"}>
            {c.title.split("\n").map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        ))}
      </div>

      <div className="idm__body">
        {rows.map((r) => {
          const sum = rowSum(r);
          const isOver = limitByQty && sum > r.qty;

          return (
            <div key={r.id} className="idm__row">
              <div className="idm__left">
                <div className="idm__label" title={r.label}>
                  {r.label}
                </div>
                <div className="idm__qty">{r.qty} ks</div>
              </div>

              {columns.map((c) => (
                <div key={c.key} className="idm__cell col-amount1">
                  <input
                    className="idm__num"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    disabled={readOnly}
                    value={r.values[c.key] ?? 0}
                    onChange={(e) => updateCell(r.id, c.key, e.target.value)}
                    aria-label={`${c.title.replace("\n", " ")} pro ${r.label}`}
                    onFocus={(e) => e.target.select()}
                  />
                  <div className="controls">
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() =>
                        updateCell(r.id, c.key, String(r.values[c.key] + 1))
                      }
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() =>
                        updateCell(r.id, c.key, String(r.values[c.key] - 1))
                      }
                    >
                      ▼
                    </button>
                  </div>
                </div>
              ))}

              {limitByQty && (
                <div
                  className={`idm__sum ${isOver ? "is-over" : ""}`}
                  title="Součet zadaných hodnot"
                >
                  {sum}/{r.qty}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ItemDispositionMatrix;